// @ts-check
/**
 * Storage — the File System layer: one folder contract, the call that opens a
 * folder, where every datum lives, and how much derived data an origin keeps.
 *
 * ## The folder contract
 *
 * **The contract is not defined here.** It is `@smotaal/pack-fs`'s
 * (`packages/@smotaal/pack-fs/lib/helpers.js`), because that package needs to say what
 * a folder is without reaching outside itself; this layer re-exports the
 * contract's functions so a consumer names one module either way. **The
 * providers and the errors are classes, and a class has one import path** —
 * `DirectoryFolder`, `BlobFolder`, `IOLayer`, `NotFoundError` and
 * `ReadOnlyError` are imported from the package, never from here.
 *
 * A store should not care whether its bytes sit in the browser's own storage,
 * in a directory the user granted, behind a local process, on a server's disk,
 * or inside one file the user just chose. It cares about listing, reading and
 * writing, and every provider answers the same seven questions:
 *
 * ```text
 * list(at)  stat(at)  readFile(at, range?)  writeFile(at, bytes)
 * makeDirectory(at)   remove(at, options)   move(from, to)
 * ```
 *
 * **Paths** are `/`-separated and relative to the folder. Empty segments and
 * `.` are dropped; `..` is refused — a folder is a boundary, and nothing
 * reaches above it. `''` is the folder itself.
 *
 * **Ranges** are inclusive at both ends, as an HTTP `Range` is, so the same
 * arguments serve a handle, a file and a server.
 *
 * **Absence is one error.** Whatever the medium says when a path is not there
 * — a `DOMException`, an `ENOENT`, a 404 — the caller sees an error whose
 * `name` is `NotFoundError`, and nothing else is ever reported that way.
 * `isNotFound(error)` is the one question to ask.
 *
 * **Conformance is a test, not a promise.** The package's `folderConformance`
 * runs the same cases over every provider, so "behaves like the others" is
 * checked rather than assumed.
 *
 * | Provider | Defined in | Medium |
 * | --- | --- | --- |
 * | `DirectoryFolder` | the package's `folders/` | a `FileSystemDirectoryHandle` — the browser's own storage or a granted directory, with the fast write path built in |
 * | `BlobFolder` | the package's `folders/` | one file, read by range, read-only |
 * | `FileFolder` | the package's `folders/file-folder.js` | a directory on disk, through `node:fs` |
 * | anything else | its own owner's | a provider is an object answering the seven calls; `openStorage` takes it by name |
 *
 * **Two calls beyond the seven are optional**, and a medium that has them says
 * so by having them: `fileOf(at)` hands out a reference to stored bytes, and
 * `seekableSink(at)` writes at an offset. The package's `IOLayer` is what
 * asks; nothing in this layer chooses a write path of its own.
 *
 * **A provider is passed in, never registered.** `openStorage` holds no table
 * of its own: a caller that has more than the browser's storage hands its
 * providers in with the call, so nothing here reaches for a global and no two
 * callers share one.
 *
 * ```js
 * const folder = await openStorage({ subdirectory: 'jobs' });
 *
 * const folder = await openStorage({
 *     subdirectory: 'jobs', prefer: 'elsewhere', credential,
 *     providers: { elsewhere: options => options.credential ? buildFolder(options.credential) : null },
 * });
 * ```
 *
 * ## The tiers — every datum's stated home, as code
 *
 * **The developer's ruling (2026-09-07, verbatim)** — the storage document's
 * §4.4, *"On the storage tiers"*:
 *
 * > SessionStorage works for the same tab, even with reload, and what I am
 * > saying is that when we are overhauling storage across all the different
 * > apps, we will need to carefully consider if something belongs in
 * > SessionStorage, LocalStorage, IndexDB (which I am not a fan of especially
 * > with OPFS and LocalStorage being the bare minimum for supported browsers)…
 * > etc.
 *
 * So there are four tiers, and **OPFS and LocalStorage are the baseline** every
 * supported browser must satisfy. The other two are asked for by name and
 * answered with what this context actually has:
 *
 * | Tier | Medium | What it holds | Lifetime |
 * | --- | --- | --- | --- |
 * | `session` | SessionStorage | per-tab state | one tab; survives a reload, dies with the tab |
 * | `local` | LocalStorage | small durable settings | the profile, until cleared |
 * | `opfs` | the origin private file system | content, derived data, the journal and the counters under `.storage/` | the origin, subject to eviction when it is not persisted |
 * | `handles` | IndexedDB | **a directory handle, and nothing else, ever** | the profile, until cleared |
 *
 * **IndexedDB is the one exception, and it is one value wide.** A
 * `FileSystemDirectoryHandle` is structured-cloneable but not serialisable to
 * text, so no other tier can hold one; that — and only that — is why the tier
 * exists. Anything else that reaches for it has a home already and has not
 * looked.
 *
 * **The two `Storage` tiers are reached through one accessor, and this module
 * is not it.** `SettingsStore` (`classes/settings/settings-store.js`) is, with
 * the `Storage` passed in — `sessionStorage` for `tab-state`, `localStorage`
 * for `settings` — and nothing else: it already keeps every rule such an
 * accessor needs (a key per tool, the whole state as one JSON object, a
 * storage that cannot be read or written is a warning rather than an error
 * because persistence is a convenience), it takes its `Storage` by injection
 * so a worker's absence of one is an ordinary answer, and it carries the
 * versioned key the table names below. A second accessor beside it would be
 * two ways to reach one tier, which is the duplication this layer exists to
 * remove. **This module states where a datum lives; it stores nothing.**
 *
 * ```js
 * homeOf('journal');
 * // → { kind: 'journal', tier: 'opfs', medium: 'OPFS', path: '.storage/cleanup.json', … }
 *
 * homeOf('tab-state');
 * // → { kind: 'tab-state', tier: 'session', medium: 'SessionStorage', key: '<key>@<version>', … }
 *
 * derivedPathOf('9f86d0…');
 * // → 'derived/9f86d0…' — the ONE path the derived home's `<content hash>` stands for
 * ```
 *
 * **Nothing here reaches for a global.** Every provision `describeTiers`
 * reports on is passed in: a worker has no `Storage` at all, and a module that
 * reached for one would answer a different question depending on where it was
 * imported.
 *
 * @module helpers/storage
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1, Claude Opus 5 (code generation)
 */

/**
 * The contract itself belongs to the package that needs it — `@smotaal/pack-fs`
 * defines a folder because a pack is written to one, and a package that
 * reached out to a sibling directory for its own vocabulary would not be a
 * package. This layer **consumes and re-exports its functions**; the classes
 * are the package's to import.
 */
export {
    FOLDER_CALLS, assertFolder, isNotFound, joinPath, segmentsOf,
} from '../packages/@smotaal/pack-fs/lib/helpers.js';

/** @typedef {import('../packages/@smotaal/pack-fs/lib/helpers.js').Entry} Entry */
/** @typedef {import('../packages/@smotaal/pack-fs/lib/helpers.js').Stat} Stat */
/** @typedef {import('../packages/@smotaal/pack-fs/lib/helpers.js').ByteRange} ByteRange */
/** @typedef {import('../packages/@smotaal/pack-fs/lib/helpers.js').Folder} Folder */
/** @typedef {import('../packages/@smotaal/pack-fs/lib/helpers.js').SeekableSink} SeekableSink */

/** The two sinks a `DirectoryFolder` chooses between, for a caller that names one. */
export { syncAccessSink, writableSink } from '../packages/@smotaal/pack-fs/lib/folders/folders.js';

/**
 * **How bytes move on a given medium is the package's question too**, and
 * `@smotaal/pack-fs`'s `io.js` answers it: which sink a folder offers, whether
 * it can hand out a reference, how an archive is best written out. A consumer
 * of this layer that needs to move bytes asks the layer it is given rather
 * than reaching for a write path of its own — the boundary test
 * (`tests/boundary.test.js`) is the gate on that.
 */
export { probeFolder } from '../packages/@smotaal/pack-fs/lib/io.js';

import { assertFolder, segmentsOf } from '../packages/@smotaal/pack-fs/lib/helpers.js';
import { DirectoryFolder } from '../packages/@smotaal/pack-fs/lib/folders/folders.js';

// ========================================
// openStorage — which provider a store gets
// ========================================

/**
 * @typedef {(options: Record<string, any> & { subdirectory?: string }) => (
 *   Folder | null | undefined | Promise<Folder | null | undefined>
 * )} StorageProvider
 *   Answers with a folder, or with nothing when it cannot serve this call —
 *   an unauthorised backend, a medium this browser lacks — in which case the
 *   browser's own storage answers instead.
 */

/**
 * Opens the folder a store should use.
 *
 * `prefer` names one of the `providers` the caller passed in. That provider is
 * asked first and given the whole option bag, so it can read whatever it needs
 * — a grant, a base URL, a key — without this layer knowing what any of it
 * means. If it is absent, or answers with nothing, the browser's own storage
 * answers.
 *
 * Nothing here asks a user for anything. A provider that needs consent must
 * already hold it: consent is a decision, and a decision is never taken on a
 * store's behalf.
 *
 * @param {{
 *   subdirectory?: string,
 *   prefer?: string,
 *   providers?: Record<string, StorageProvider>,
 * } & Record<string, any>} [options]
 * @returns {Promise<Folder>} A folder answering the seven calls.
 */
export async function openStorage(options = {}) {
    const { subdirectory, prefer = 'opfs', providers } = options;
    const provider = prefer && prefer !== 'opfs' ? providers?.[prefer] : undefined;
    if (provider) {
        const folder = await provider(options);
        if (folder) return assertFolder(folder);
    }
    return DirectoryFolder.openOPFS({ subdirectory });
}

/**
 * The branch a path belongs to — its first `depth` segments — or `null` above
 * a branch. A branch is the unit that lives in one tier of a library
 * (`classes/storage/library-folder.js`); how many segments name one is
 * the caller's to say.
 *
 * @param {string} at
 * @param {number} depth
 * @returns {string | null}
 */
export function branchOf(at, depth) {
    const segments = segmentsOf(at);
    return segments.length >= depth ? segments.slice(0, depth).join('/') : null;
}

// ========================================
// The four tiers
// ========================================

/** @typedef {'session' | 'local' | 'opfs' | 'handles'} TierName */

/**
 * @typedef {{
 *   tier: TierName,
 *   medium: string,
 *   holds: string,
 *   lifetime: string,
 *   baseline: boolean,
 * }} Tier
 *   `baseline` marks the two a supported browser must have — the developer's
 *   *"OPFS and LocalStorage being the bare minimum"*. A tier that is not the
 *   baseline may be absent, and a caller has to say what it does then.
 */

/** The four mediums, as the developer named them. */
export const TIERS = Object.freeze(/** @type {Record<TierName, Tier>} */ ({
    session: Object.freeze({
        tier: 'session',
        medium: 'SessionStorage',
        holds: 'per-tab state — what the operator is looking at, not what they own',
        lifetime: 'one tab; survives a reload, dies with the tab',
        baseline: false,
    }),
    local: Object.freeze({
        tier: 'local',
        medium: 'LocalStorage',
        holds: 'small durable settings',
        lifetime: 'the browser profile, until it is cleared',
        baseline: true,
    }),
    opfs: Object.freeze({
        tier: 'opfs',
        medium: 'OPFS',
        holds: 'content and derived data — the record, and the cleanup journal and usage counters under `.storage/`',
        lifetime: 'the origin; evictable while the origin is not persisted',
        baseline: true,
    }),
    handles: Object.freeze({
        tier: 'handles',
        medium: 'IndexedDB',
        holds: 'a granted directory handle, and nothing else, ever',
        lifetime: 'the browser profile, until it is cleared',
        baseline: false,
    }),
}));

/** The tier names, in the order the table states them. */
export const TIER_NAMES = Object.freeze(/** @type {TierName[]} */ (Object.keys(TIERS)));

// ========================================
// Every datum's home
// ========================================

/**
 * @typedef {{
 *   kind: string,
 *   tier: TierName,
 *   medium: string,
 *   path: string | null,
 *   key: string | null,
 *   why: string,
 * }} Home
 *   `path` is the convention **relative to the caller's own folder**, since
 *   this layer never names one; `''` is that folder itself. `key` is the key
 *   grammar on a `Storage` tier, which is `SettingsStore`'s. Each is `null`
 *   where the tier is not addressed that way — IndexedDB is addressed by
 *   neither, and holds one value.
 */

/**
 * The tiers table of the plan's §2.3, kind by kind.
 *
 * The kinds are this layer's own vocabulary. What an application calls the
 * thing it is storing is the application's business: it asks for the home of
 * `content` and names its own folder, and this table never learns what was in
 * it. That is the same division `openStorage` keeps for providers.
 *
 * | Kind | Tier | Where |
 * | --- | --- | --- |
 * | `content` | `opfs` | the caller's own folder |
 * | `derived` | `opfs` | `derived/<content hash>/…` |
 * | `derived-packs` | `opfs` | the caller's own folder |
 * | `journal` | `opfs` | `.storage/cleanup.json` |
 * | `operations` | `opfs` | `.storage/operations.json` |
 * | `cursor` | `opfs` | `.storage/reconciliation.json` |
 * | `usage` | `opfs` | `.storage/usage.json` |
 * | `settings` | `local` | `<key>@<version>` |
 * | `tab-state` | `session` | `<key>@<version>` |
 * | `directory-handle` | `handles` | one store, one value kind |
 *
 * **Two kinds carry no path of their own, and for the same reason.** `content`
 * lives in the caller's folder because this layer never names one, and
 * `derived-packs` does too: where a caller keeps packs it made from its own
 * derived data is the caller's to say, and settling it here would settle a
 * question the design has not.
 *
 * @type {Readonly<Record<string, Home>>}
 */
export const HOMES = Object.freeze({
    content: Object.freeze({
        kind: 'content',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '',
        key: null,
        why: 'the record; one copy',
    }),
    derived: Object.freeze({
        kind: 'derived',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: 'derived/<content hash>/',
        key: null,
        why: 'rebuildable; keyed by content alone',
    }),
    'derived-packs': Object.freeze({
        kind: 'derived-packs',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '',
        key: null,
        why: 'a pack in its own right — an artefact of the derived data, exportable and importable on its own',
    }),
    journal: Object.freeze({
        kind: 'journal',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '.storage/cleanup.json',
        key: null,
        why: 'must survive the host that wrote it',
    }),
    operations: Object.freeze({
        kind: 'operations',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '.storage/operations.json',
        key: null,
        why: 'must survive the host that wrote it',
    }),
    cursor: Object.freeze({
        kind: 'cursor',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '.storage/reconciliation.json',
        key: null,
        why: 'must survive the host that wrote it',
    }),
    usage: Object.freeze({
        kind: 'usage',
        tier: 'opfs',
        medium: TIERS.opfs.medium,
        path: '.storage/usage.json',
        key: null,
        why: 'must survive the host that wrote it',
    }),
    settings: Object.freeze({
        kind: 'settings',
        tier: 'local',
        medium: TIERS.local.medium,
        path: null,
        key: '<key>@<version>',
        why: 'small, durable, baseline',
    }),
    'tab-state': Object.freeze({
        kind: 'tab-state',
        tier: 'session',
        medium: TIERS.session.medium,
        path: null,
        key: '<key>@<version>',
        why: 'survives a reload, dies with the tab — the developer’s own example',
    }),
    'directory-handle': Object.freeze({
        kind: 'directory-handle',
        tier: 'handles',
        medium: TIERS.handles.medium,
        path: null,
        key: null,
        why: 'no other tier can hold a handle — the one exception',
    }),
});

/** Every kind the table answers for, in the order it states them. */
export const DATUM_KINDS = Object.freeze(Object.keys(HOMES));

/**
 * Where a datum of this kind lives.
 *
 * **An unknown kind is refused by name.** Guessing would put a datum somewhere
 * nobody decided, which is the whole thing this table exists to stop; the
 * refusal names what was asked for and lists what the table answers, so the
 * caller either uses a kind or the table gains one deliberately.
 *
 * @param {string} kind
 * @returns {Home}
 */
export function homeOf(kind) {
    const home = Object.prototype.hasOwnProperty.call(HOMES, kind) ? HOMES[kind] : undefined;
    if (!home) {
        throw new TypeError(
            `No home is stated for “${kind}”. The kinds with a home are: ${DATUM_KINDS.join(', ')}. `
            + 'A datum without one is a decision nobody has taken — take it in the tiers table, not here.');
    }
    return home;
}

/**
 * The folder one derived entry lives in, from the content hash it is keyed by.
 *
 * The derived home's path is stated as `derived/<content hash>/`, and
 * `<content hash>` is a placeholder rather than a segment — so a caller that
 * wanted the real path had to either interpolate into the table's own string or
 * spell `derived/` itself, and spelling it is the storage decision the fourth
 * rule of `tests/boundary.test.js` forbids an application to take. This is the
 * table answering its own placeholder.
 *
 * **The key is a whole segment and nothing else.** A content hash is hexadecimal
 * and could never be anything else, but this is reached with whatever a caller
 * calls its key, and a key carrying `/` or `..` would address a folder outside
 * the cache — silently, since every folder in this layer joins paths without
 * looking. So it is refused by name instead. `.` and `..` are refused for the
 * same reason `joinPath` refuses them.
 *
 * @param {string} key the content hash the entry is keyed by
 * @returns {string} the entry's folder, relative to the origin's own root
 */
export function derivedPathOf(key) {
    const named = `${key ?? ''}`;
    if (!named || named === '.' || named === '..' || /[/\\]/u.test(named)) {
        throw new TypeError(
            `“${named}” is not a key for the derived home. A key is ONE path segment — it is what the `
            + 'table\'s `derived/<content hash>/` stands for — so it cannot be empty, cannot be `.` or `..`, '
            + 'and cannot carry a separator: any of those would address a folder outside the cache.');
    }
    // The prefix comes from the table rather than from a literal here, so a
    // home that moves takes this with it.
    const [prefix] = `${HOMES.derived.path}`.split('/');
    return `${prefix}/${named}`;
}

/**
 * A byte length as a reader reads it: `0 B`, `512 B`, `1.5 KB`, `412.3 MB`,
 * `1.2 GB`, in units of 1024: the one form every status badge and the Debug
 * panel's sizes print (the developer, 2026-11-03: the blob URL badge sits
 * beside `34.66 GB used of 44.66 GB`).
 *
 * @param {number} byteLength
 * @returns {string}
 */
export function formatBytes(byteLength) {
    if (!Number.isFinite(byteLength) || byteLength <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(units.length - 1, Math.floor(Math.log(byteLength) / Math.log(1024)));
    return `${(byteLength / 1024 ** exponent).toFixed(exponent ? 1 : 0)} ${units[exponent]}`;
}

// ========================================
// How much derived data an origin keeps
// ========================================

const MEBIBYTE = 1024 * 1024;
const GIBIBYTE = 1024 * MEBIBYTE;

/**
 * The bounds on the derived home, and where each number comes from.
 *
 * **`measuredEntryBytes` is a measurement, not a guess**: one thumbnail of the
 * contract both tools produce (PNG, 8-bit, 3-channel, white matte, 640-pixel
 * edge) built from a 4096 × 3600 segment came to **825,901 bytes** on Chromium
 * (`experiments/storage/thumbnail-cap/output/2026-09-08-003`,
 * `measurements.usage.thumbnailBytes`) — the same number in three separate runs,
 * so it is the size rather than a sample of it. Every bound below is stated in
 * terms of what it buys at that size, so a reader can check the arithmetic
 * rather than take the constant on trust.
 *
 * | | Best-effort | Persisted |
 * | --- | --- | --- |
 * | share of the origin's quota | 5% | 10% |
 * | floor | 256 MiB — ≈ 320 thumbnails, a folder of 200 segments browsed twice over | 1 GiB — ≈ 1,300 |
 * | ceiling | 1 GiB | 8 GiB |
 * | entries | 1,500 | 20,000 |
 *
 * **Why a share of quota and not a fixed size.** The quotas measured in E1
 * (storage document §2, P3 and P5) span three orders of magnitude — 1.0 GiB in
 * an ephemeral Chromium context, 10 GiB in an unpersisted Firefox, 931 GiB in a
 * persisted one — so one number would be either useless on the small ones or
 * reckless on the large. **Why a floor.** An origin with almost no room still
 * has to be able to browse a folder, and a cache pruned below a working set
 * costs more in rebuilds than it saves in bytes. **Why a ceiling.** A terabyte
 * of quota is not a reason to keep a terabyte of data that can be rebuilt from
 * the record.
 *
 * **Why the entry bound as well as the byte bound.** The unit the cache prunes
 * is a content hash's whole folder, and what is in one is the application's
 * business — a thumbnail alone in one tool, a thumbnail and a full rendition in
 * another. The byte bound governs the big case and the entry bound governs the
 * small one, and whichever bites first is the bound.
 *
 * These are the bounds taken from Playwright's quotas; what the developer's
 * own browsers report may move them.
 */
export const DERIVED_BOUNDS = Object.freeze({
    measuredEntryBytes: 825_901,
    measuredAt: '2026-09-08, Chromium 147.0.7727.15, a 640-pixel edge from a 4096 × 3600 segment',
    bestEffort: Object.freeze({
        entries: 1500,
        shareOfQuota: 0.05,
        floorBytes: 256 * MEBIBYTE,
        ceilingBytes: 1 * GIBIBYTE,
    }),
    persisted: Object.freeze({
        entries: 20_000,
        shareOfQuota: 0.10,
        floorBytes: 1 * GIBIBYTE,
        ceilingBytes: 8 * GIBIBYTE,
    }),
});

/**
 * How much derived data this origin may keep, from what it is right now.
 *
 * The policy is `PersistentStorageManager.policyFor('derived')`'s answer, taken
 * as it comes: its `purpose` and `aggressive` fields are carried rather than
 * consulted, because two readings of one origin is how two answers happen. Only
 * `persisted` decides, and `quotaBytes` scales.
 *
 * `quotaBytes: null` is *"this origin would not say"*, which is neither zero nor
 * infinity — the safe reading of it is the smallest bound rather than the
 * largest.
 *
 * @param {{ persisted?: boolean, quotaBytes?: number | null }} policy
 * @returns {{ entries: number, bytes: number, reason: string }}
 */
export function boundsFor({ persisted = false, quotaBytes = null } = {}) {
    const band = persisted === true ? DERIVED_BOUNDS.persisted : DERIVED_BOUNDS.bestEffort;
    const state = persisted === true ? 'a persisted origin keeps derived data generously' : 'a best-effort origin keeps it bounded and rebuildable';

    if (typeof quotaBytes !== 'number' || !Number.isFinite(quotaBytes) || quotaBytes <= 0) {
        return Object.freeze({
            entries: band.entries,
            bytes: band.floorBytes,
            reason: `${state}; this origin would not say what its quota is, so the floor stands — `
                + `${describeBytes(band.floorBytes)}, about ${Math.round(band.floorBytes / DERIVED_BOUNDS.measuredEntryBytes)} `
                + 'entries at the measured size',
        });
    }

    const share = quotaBytes * band.shareOfQuota;
    const bytes = Math.min(Math.max(share, band.floorBytes), band.ceilingBytes);
    const which = bytes === band.floorBytes && share < band.floorBytes ? 'the floor'
        : bytes === band.ceilingBytes && share > band.ceilingBytes ? 'the ceiling'
            : `${Math.round(band.shareOfQuota * 100)}% of the quota`;
    return Object.freeze({
        entries: band.entries,
        bytes,
        reason: `${state}; ${which} of ${describeBytes(quotaBytes)} is ${describeBytes(bytes)}, about `
            + `${Math.round(bytes / DERIVED_BOUNDS.measuredEntryBytes)} entries at the measured size`,
    });
}

/** @param {number} bytes */
function describeBytes(bytes) {
    if (bytes >= GIBIBYTE) return `${(bytes / GIBIBYTE).toFixed(1)} GiB`;
    if (bytes >= MEBIBYTE) return `${Math.round(bytes / MEBIBYTE)} MiB`;
    return `${bytes} bytes`;
}

// ========================================
// What THIS context has
// ========================================

/**
 * Whether a value answers the `Storage` interface — all five members, because
 * a caller that lists the medium to find what an earlier revision left needs
 * `length` and `key(index)` as much as a plain read needs `getItem`.
 *
 * @param {unknown} candidate
 */
export function isStorage(candidate) {
    const storage = /** @type {any} */ (candidate);
    return Boolean(storage)
        && typeof storage.getItem === 'function'
        && typeof storage.setItem === 'function'
        && typeof storage.removeItem === 'function'
        && typeof storage.key === 'function'
        && typeof storage.length === 'number';
}

/**
 * @typedef {{
 *   sessionStorage?: Storage | null,
 *   localStorage?: Storage | null,
 *   navigator?: { storage?: { getDirectory?: unknown } } | null,
 *   indexedDB?: { open?: unknown } | null,
 * }} TierProvisions
 *   Everything `describeTiers` reports on, passed in. A caller in a page hands
 *   its own four; a worker hands the two it has and `null` for the rest; a test
 *   hands stand-ins. Nothing is read from the scope.
 */

/**
 * @typedef {{
 *   tier: TierName,
 *   medium: string,
 *   baseline: boolean,
 *   available: boolean,
 *   reason: string,
 * }} TierAvailability
 */

/**
 * @typedef {{
 *   tiers: Record<TierName, TierAvailability>,
 *   available: readonly TierName[],
 *   missing: readonly TierName[],
 *   baseline: boolean,
 * }} TiersDescription
 *   `baseline` is the developer's *"bare minimum"*: OPFS and LocalStorage both
 *   present. A context without it is not a place this layer can keep anything,
 *   and the caller has to say what it does then.
 */

/**
 * Which tiers this context has, from what it was given.
 *
 * A worker has no `Storage` of either kind; a browser without
 * `navigator.storage.getDirectory` has no OPFS; IndexedDB is present or it is
 * not. Each answer carries the reason, so a tool can say what is missing rather
 * than only that something is.
 *
 * @param {TierProvisions} [provisions]
 * @returns {TiersDescription}
 */
export function describeTiers(provisions = {}) {
    const { sessionStorage = null, localStorage = null, navigator = null, indexedDB = null } = provisions;

    /** @param {TierName} tier @param {boolean} available @param {string} reason */
    const state = (tier, available, reason) => Object.freeze({
        tier, medium: TIERS[tier].medium, baseline: TIERS[tier].baseline, available, reason,
    });

    /** @param {TierName} tier @param {unknown} given */
    const storageState = (tier, given) => {
        if (given === null || given === undefined) {
            return state(tier, false, `no ${TIERS[tier].medium} was passed in — a worker has none`);
        }
        if (!isStorage(given)) {
            return state(tier, false,
                `what was passed for ${TIERS[tier].medium} does not answer the Storage interface`);
        }
        return state(tier, true, `${TIERS[tier].medium} answers`);
    };

    const hasOPFS = typeof (/** @type {any} */ (navigator)?.storage?.getDirectory) === 'function';
    const hasIndexedDB = typeof (/** @type {any} */ (indexedDB)?.open) === 'function';

    const tiers = Object.freeze(/** @type {Record<TierName, TierAvailability>} */ ({
        session: storageState('session', sessionStorage),
        local: storageState('local', localStorage),
        opfs: hasOPFS
            ? state('opfs', true, 'navigator.storage.getDirectory answers')
            : state('opfs', false,
                'this context has no navigator.storage.getDirectory — a plain-HTTP origin has no storage manager at all'),
        handles: hasIndexedDB
            ? state('handles', true, 'IndexedDB answers')
            : state('handles', false, 'this context has no IndexedDB, so a granted handle could not be kept'),
    }));

    const available = TIER_NAMES.filter(tier => tiers[tier].available);
    return Object.freeze({
        tiers,
        available: Object.freeze(available),
        missing: Object.freeze(TIER_NAMES.filter(tier => !tiers[tier].available)),
        baseline: tiers.opfs.available && tiers.local.available,
    });
}
